import { observer } from "mobx-react-lite";
import React, { useEffect, useRef, useState } from "react";
import SimpleBar from "simplebar-react";

import useWindowSize from "../hooks/useWindowSize";

const Tabs = observer(
  (props: {
    titles: string[];
    initialIndex?: number;
    tabIndex?: number; // Optionally control tab index from parent component
    onChangeTabIndex?: (index: number) => void;
    headerClassName?: string;
    renderContent: (tabIndex: number) => JSX.Element;
  }) => {
    const headerScrollRef = useRef<HTMLDivElement>(null);
    const headerTabsRef = useRef<HTMLDivElement>(null);
    const windowSize = useWindowSize();

    const [selectedIndex, setSelectedIndex] = useState(
      props.initialIndex === undefined ? 0 : props.initialIndex
    );

    useEffect(() => {
      // Need to re-calculate the width of the tab scroll
      calculateHeaderScrollWidth();
    }, [headerTabsRef.current, windowSize.width]);

    const calculateHeaderScrollWidth = () => {
      if (headerScrollRef.current) {
        headerScrollRef.current.style.width = "2000000px";
        requestAnimationFrame(() => {
          if (headerTabsRef.current) {
            let width = 0;
            for (let i = 0; i < headerTabsRef.current.children.length; i++) {
              width +=
                headerTabsRef.current.children[i].getBoundingClientRect()
                  .width + 40;
            }
            if (headerScrollRef.current) {
              headerScrollRef.current.style.width = width + "px";
            }
          }
        });
      }
    };

    useEffect(() => {
      if (props.tabIndex !== undefined && props.tabIndex !== selectedIndex) {
        setSelectedIndex(props.tabIndex);
        scrollTabIntoView(props.tabIndex);
      }
    }, [props.tabIndex]);

    const scrollTabIntoView = (index: number) => {
      // If the tab is outside of the scroll area, scroll it into view
      const headerContainer = document.querySelector(
        ".pyht-tabs .simplebar-content-wrapper"
      ) as HTMLDivElement;
      const targetEl = document.querySelectorAll(
        ".pyht-tabs .tabs-header-tabs .tab"
      );
      if (targetEl.length > index && headerContainer) {
        const targetTab = targetEl[index] as HTMLDivElement;
        const elLeft = targetTab.offsetLeft + targetTab.offsetWidth;
        const elParentLeft =
          headerContainer.offsetLeft + headerContainer.offsetWidth;

        // check if element not in view
        if (
          elLeft + targetTab.offsetWidth >=
          elParentLeft + headerContainer.scrollLeft
        ) {
          headerContainer.scrollLeft = elLeft - elParentLeft + 20;
        } else if (
          elLeft <=
          headerContainer.offsetLeft + headerContainer.scrollLeft
        ) {
          headerContainer.scrollLeft =
            targetTab.offsetLeft - headerContainer.offsetLeft - 20;
        }
      }
    };

    const onChangeTabIndex = (index: number) => {
      setSelectedIndex(index);
      scrollTabIntoView(index);
      if (props.onChangeTabIndex) {
        props.onChangeTabIndex(index);
      }
    };

    return (
      <div className={"pyht-tabs"}>
        <div
          className={`tabs-header ${
            props.headerClassName ? props.headerClassName : ""
          }`}
        >
          <SimpleBar autoHide={false}>
            <div className={"tabs-header-inner"} ref={headerScrollRef}>
              <div className={"tabs-header-tabs"} ref={headerTabsRef}>
                {props.titles.map((title, index) => {
                  return (
                    <p
                      role={"button"}
                      key={title + index}
                      className={`tab section-label ${
                        selectedIndex === index ? "active" : ""
                      }`}
                      onClick={() => onChangeTabIndex(index)}
                    >
                      {title}
                    </p>
                  );
                })}
              </div>
            </div>
          </SimpleBar>
        </div>
        <div className={"tabs-content"}>
          {props.renderContent(selectedIndex)}
        </div>
      </div>
    );
  }
);

export default Tabs;
